/* Dynamic Plot code copyright 2023 (c) Tuva Labs Inc. */
/* dynPreferences.js split from dynMiscallany.js on 2023-12-21 by CDM */

import { defaultFontSizeMultiplier, defaultFontSizeMultiplierForJr } from '../../utils/constants';

export const wrapDynPreferences = function(Dyn) {

    /**
     * Preferences for the user space.
     * For variables which are used by multiple components,
     * so we can save and restore them independently.
     */
    class DynPreferences {

        _isAttributeContextMenu = false;       // set to false, to start with for the dropdown/context menu for casecard
        _useAnimation = true;                  // use false to disable animation for accessibility purposes
        _colorScheme = "colorsLikeTinkerPlots";  // see Dyn.colorUtil.switchColorTheme() for valid values.
        _theme = "tuva";                         // to store active theme. e.g: reverse-contrast, yellow-on-blue, black-on-rose
        _fontSizeMultiplier = 1.0;             // e.g. 1.2 === 120% of standard size
        //_caseIconSize = 0;                     // 0 is standard size for case
        _precision = 0;                        // user-specified +/- precision to add to all statistical values (0=standard significant digits)
        //_isStatLabelOn = false;                // show visible label for Mean, Median, etc. in Graph?
        //_isCustomIconOn = false;
        _wantY2Axis = false;
        _isDisplayAttributeSettingOn = true;   // by default show the display attribute settings eye icon, to show hide attributes
        _caseCardCollapsible = false;    // by default show the case card is displayed in expand mode
        _isPlotTitleVisible = true;            // by default show the plot title
        _isCustomAttributeOn = true;           // by default show option to add custom attribute in case card
        _viewOnly = false;                     // by default view only is false
        _tour = false;
        _mode = '';                              // by default mode is empty. e.g: 'jr', 'elem', 'middle', 'high', 'draw'
        _plotReset = true;                     // by default plot reset is true - show the button

        constructor() {
        }

        save() {
            "use strict";
            var prefData = {
                attributeContextMenu: this._isAttributeContextMenu, //for accessibility allow option of haveing user switch
                animation: this._useAnimation, // Boolean
                displayAttributeSetting: this.isDisplayAttributeSettingOn(),
                customAttribute: this.isCustomAttributeOn(),
                wantY2Axis: this._wantY2Axis
            };
            // redundant and removed for v.3.2.0+, settings are saved with PlotView.save() instead
            // if( Dyn.plotView ) {
            //     // if no plotView, do not save or restore these parameters that depend on plotView
            //     // If we want to save/restore these after switching to Play View, consider moving them from PlotView to Preferences
            //     prefData.gridLines = this.isGridLinesOn();
            //     prefData.statsLabel = this.isStatsLabelOn();
            //     prefData.customIconOn = this.isCustomIconOn();
            //     if( this._caseIconSize !== 0 )
            //         prefData.caseIconSize = this._caseIconSize;
            // }
            // save values only if not the default, so that defaults can be overriden in later versions
            if( !this.isPlotTitleVisible() )
                prefData.plotTitle = false;
            if( this._colorScheme !== "colorsLikeTinkerPlots" )
                prefData.colorScheme = this._colorScheme;
            if( this._theme !== "tuva" )
                prefData.theme = this._theme;
            if( this._fontSizeMultiplier !== (this.mode === 'jr' ? defaultFontSizeMultiplierForJr : defaultFontSizeMultiplier))
                prefData.fontSizeMultiplier = this._fontSizeMultiplier;
            if( this._precision !== 0 )
                prefData.precision = this._precision;
            if( this._mode )
                prefData.mode = this._mode;
            if( this._plotReset !== true )
                prefData.plotReset = this._plotReset;
            if( this._caseCardCollapsible === true )
                prefData.caseCardCollapsible = this._caseCardCollapsible;
            if( this._viewOnly === true ) {
                prefData.viewOnly = this._viewOnly;
                // Remove irrelevant properties when viewOnly is true
                delete prefData.customAttribute;
                delete prefData.displayAttributeSetting;
                delete prefData.animation;
                delete prefData.wantY2Axis;
                delete prefData.attributeContextMenu;
                delete prefData.plotReset;
            }
            if( this._tour === true) {
                prefData.tour = true;
            }

            return prefData;
        }

        restore( prefData ) {
            if( prefData ) {
                if( prefData.attributeContextMenu !== undefined )
                    this._isAttributeContextMenu = prefData.attributeContextMenu;
                if( prefData.animation != undefined )
                    this._useAnimation = (prefData.animation !== 'off' && prefData.animation !== false);
                // redundant and removed for v.3.2.0+, setting is restored with PlotView.restore() instead
                // if( prefData.gridLines )
                //     this._isGridLinesOn = prefData.gridLines;
                if( prefData.displayAttributeSetting )
                    this._isDisplayAttributeSettingOn = prefData.displayAttributeSetting;
                if( prefData.caseCardCollapsible )
                    this._caseCardCollapsible = prefData.caseCardCollapsible;
                if( prefData.plotTitle )
                    this._isPlotTitleVisible = prefData.plotTitle;
                if( prefData.customAttribute )
                    this._isCustomAttributeOn = prefData.customAttribute;
                // redundant and removed for v.3.2.0+, setting is restored with PlotView.restore() instead
                // if( prefData.statsLabel )
                //     this._isStatLabelOn = prefData.statsLabel;
                // if( prefData.customIconOn )
                //     this._isCustomIconOn = prefData.customIconOn;
                if( prefData.colorScheme )
                    this._colorScheme = prefData.colorScheme;
                if( prefData.theme )
                    this._theme = prefData.theme;
                if( prefData.fontSizeMultiplier )
                    this._fontSizeMultiplier = prefData.fontSizeMultiplier;
                // redundant and removed for v.3.2.0+, setting is restored with PlotView.restore() instead
                // if( prefData.caseIconSize )
                //     this._caseIconSize = prefData.caseIconSize;
                if( prefData.precision )
                    this._precision = prefData.precision;
                if( prefData.wantY2Axis != undefined )
                    this._wantY2Axis = prefData.wantY2Axis;
                if( prefData.viewOnly === true )
                    this._viewOnly = prefData.viewOnly;
                if( prefData.tour === true )
                    this._tour = prefData.tour;
                if( prefData.mode )
                    this._mode = prefData.mode;
                if (prefData.plotReset)
                    this._plotReset = prefData.plotReset;
            }
            // preferences are typically restored before the plot is created, so no notification needed
        }

        toggleAttributeContextMenu() {
            this._isAttributeContextMenu = !this._isAttributeContextMenu;
            // Dyn.mediator.publish( "pref:attributecontextMenuOnOff", {
            //     value: this._isAttributeContextMenu
            // });
        }

        setAttributeContextMenu( trueFalse ) {
            this._isAttributeContextMenu = Boolean( trueFalse );
            // Dyn.mediator.publish( "pref:attributeContextMenuOnOff", { value: this._isAttributeContextMenu } );
        }

        isAttributeContextMenu() {
            return this._isAttributeContextMenu;
        }

        toggleAnimationSetting() {
            Dyn.mediator.publish( "pref:animationOnOff", {
                value: !this._useAnimation
            } );
        }

        setAnimationOn( trueFalse ) {
            this._useAnimation = Boolean( trueFalse );
            Dyn.mediator.publish( "pref:animationOnOff", { value: this._useAnimation } );
        }

        isAnimationOn() {
            return this._useAnimation;
        }

        setPlotResetOn( trueFalse ) {
            this._plotReset = Boolean( trueFalse );
        }

        isPlotResetOn() {
            return this._plotReset;
        }

        setViewOnly( trueFalse ) {
            this._viewOnly = Boolean( trueFalse );
        }

        isViewOnly() {
            return this._viewOnly;
        }

        setTour( trueFalse ) {
            this._tour = Boolean( trueFalse );
        }

        setGridLinesOn( trueFalse ) {
            //this._isGridLinesOn = Boolean( trueFalse );
            const controlType = Dyn.EPlotControl.kControlsGridLines;
            let plotView = Dyn.plotView;
            if( plotView ) {
                if( !trueFalse ) {
                    if( plotView.plotShaper.hasElement( controlType ) )
                        plotView.removePlot( controlType );
                } else {
                    if( plotView.plotShaper.hasElement( controlType ) ) {
                        console.log( "Already has a grid line, notifications need to be fixed" );
                    }
                    plotView.addPlot( new Dyn.GridLinesElement( Dyn ) );
                }
            }
        }

        isGridLinesOn() {
            return Boolean(Dyn.plotView?.plotShaper.hasElement( Dyn.EPlotControl.kControlsGridLines ));
        }

        setDisplayAttributeSettingOn( trueFalse ) {
            this._isDisplayAttributeSettingOn = Boolean( trueFalse );
            // Dyn.mediator.publish( "pref:displayAttributeSettingOnOff", { value: this._isDisplayAttributeSettingOn } );
        }

        setPlotTitleVisible( trueFalse ) {
            this._isPlotTitleVisible = Boolean( trueFalse );
        }

        isDisplayAttributeSettingOn() {
            return this._isDisplayAttributeSettingOn;
        }

        setCaseCardCollapsible( trueFalse ) {
            this._caseCardCollapsible = Boolean( trueFalse );
        }

        isCaseCardCollapsible() {
            return this._caseCardCollapsible;
        }

        isPlotTitleVisible() {
            return this._isPlotTitleVisible;
        }

        setCustomAttributeOn( trueFalse ) {
            this._isCustomAttributeOn = Boolean( trueFalse );
            // Dyn.mediator.publish( "pref:customAttributeOnOff", { value: this._isCustomAttributeOn } );
        }

        isCustomAttributeOn() {
            return this._isCustomAttributeOn;
        }

        setWantY2Axis( trueFalse, source = "" ) {
            this._wantY2Axis = trueFalse;
            Dyn.mediator.publish( "pref:wantY2Axis", { wantY2Axis: trueFalse, source: source } );  // then tell views to redraw stats
        }

        wantY2Axis() {
            return this._wantY2Axis;
        }

        isStatsLabelOn() {
            return Boolean(Dyn.plotView?.plotShaper.showLabelsOnStats);
        }

        toggleStatsLabel() {
            this.setStatsLabelOn( ! this.isStatsLabelOn() );
        }

        setStatsLabelOn( trueFalse ) {
            let plotShaper = Dyn.plotView?.plotShaper;
            if( plotShaper ) {
                plotShaper.showHideLabelsOnStats( trueFalse );
                Dyn.mediator.publish( "toolBar:showHideLabelsOnStats", { showLabelsOnStats: plotShaper.showLabelsOnStats } );
            }
        }

        isCustomIconOn() {
            return Dyn.plotView?.plotShaper.iconStyler.isCustomCaseIconEnabled();
        }

        toggleCustomIcon() {
            this.setCustomIconOn( !this.isCustomIconOn() );
        }

        setCustomIconOn( trueFalse ) {
            //this._isCustomIconOn = Boolean( trueFalse );
            Dyn.plotView?.plotShaper.iconStyler.setCustomCaseIconEnabled( trueFalse );
            Dyn.mediator.publish( "toolBar:enableDisableCustomIcon", {} );
        }

        setColorScheme( schemeName ) {
            if( Dyn.colorUtil.switchColorTheme( schemeName ) ) {
                this._colorScheme = schemeName;
                Dyn.mediator.publish( "pref:newColorScheme", { name: schemeName, id: Dyn.colorUtil.theme.id } );  // then tell views to redraw colors
            }   // else ignore invalid schemeNames; switchColorTheme() will assert for debugging purposes
        }

        getColorScheme() {
            return this._colorScheme;
        }

        setMode( mode ) {
            if(mode === 'jr') {
                // Set fontSize to 18 for jr. mode
                this.setFontSizeMultiplier(defaultFontSizeMultiplierForJr);
            }
            this.mode = mode;
        }

        getMode() {
            return this._mode;
        }

        setTheme( theme, subTheme=null ) {
            this._theme = theme;
            Dyn.mediator.publish( "pref:newTheme", { themeName: theme, subTheme: subTheme } );
        }

        getTheme() {
            return this._theme;
        }

        setFontSizeMultiplier( m, notify=true ) {
            if( Dyn.mathUtil.isFinite( m ) ) {
                this._fontSizeMultiplier = m;
                if( notify ) {
                    Dyn.mediator.publish( "pref:newFontSize", { multiplier: this._fontSizeMultiplier } );  // then tell views to redraw colors
                }
            }
        }

        getFontSizeMultiplier() {
            return this._fontSizeMultiplier;
        }

        setCaseIconSize( s) {
            if( Dyn.mathUtil.isFinite( s ) ) {
                Dyn.plotView?.plotShaper.iconStyler.setCustomCaseIconSize( s );
                Dyn.mediator.publish( "pref:newCaseIconSize", { size: s } );  // then tell views to update case icon's size
            }
        }

        getCaseIconSize() {
            return Dyn.plotView?.plotShaper.iconStyler.getActualCaseIconSize();
        }

        /**
         * Get the font size adjusted by Dyn.preferences._fontSizeMultiplier.
         * Callers should be careful *not* to store this adjusted font size with individual elements,
         * so that after restore the font size preference continues to be dynamic.
         */
        getFontSize( standardSize = 12 ) {
            let s = Math.round( this._fontSizeMultiplier * standardSize ); // integer multiple of standard size
            return s;
        }

        /**
         * user-specified precision preference. Typically combines with attribute.getNumericDecimals()
         * and averageElement.getPrecision() to get final precision/rounding of a displayed statistic.
         * Valid values are integers +/- Dyn.mathUtil.maxFixedPrecision.
         * @param p
         * @param source - optional source to avoid excess recalculating
         */
        setPrecision( p, source = "" ) {
            if( Dyn.mathUtil.isNumber( p ) && Dyn.mathUtil.isInteger( p ) ) {
                this._precision = Dyn.mathUtil.clipToMinMax( p, -Dyn.mathUtil.maxFixedPrecision, Dyn.mathUtil.maxFixedPrecision );
                Dyn.mediator.publish( "pref:newPrecision", { precision: this._precision, source: source } );  // then tell views to redraw stats
            }
        }

        getPrecision() {
            return this._precision;
        }
    } // class dynPreferences

    Dyn.preferences = new DynPreferences();
}

